import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Self-contained i18n so the CTA can render outside any LanguageProvider.
const STRINGS = {
  tr: { label: "Teklif Al",        msg: "Merhaba, web sitenizden ulaşıyorum. Teklif almak istiyorum." },
  en: { label: "Get a Quote",      msg: "Hi, I'm reaching you from your website. I'd like to get a quote." },
  es: { label: "Obtener Cotización", msg: "Hola, les escribo desde su sitio web. Quisiera recibir una cotización." },
  de: { label: "Angebot anfordern", msg: "Hallo, ich kontaktiere Sie über Ihre Website. Ich hätte gerne ein Angebot." },
  fr: { label: "Demander un devis", msg: "Bonjour, je vous contacte depuis votre site web. Je souhaite recevoir un devis." },
  ar: { label: "احصل على عرض",      msg: "مرحبا، أتواصل معكم من موقعكم. أود الحصول على عرض سعر." },
};
const SUPPORTED = Object.keys(STRINGS);

function detectLangFromPath(pathname = "/") {
  const m = pathname.split("/").filter(Boolean)[0];
  return SUPPORTED.includes(m) ? m : "tr";
}

/**
 * Sticky animated WhatsApp "Get a Quote" CTA — fixed bottom-end on every page.
 * Visibility is controlled by the `show_whatsapp` setting in admin.
 */
export default function WhatsAppFloatingCTA() {
  const { pathname } = useLocation();
  const lang = detectLangFromPath(pathname);
  const isAdmin = pathname.startsWith("/admin");
  const t = STRINGS[lang] || STRINGS.tr;

  const [visible, setVisible] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [wiggle, setWiggle] = useState(false);
  const [showWA, setShowWA] = useState(false);
  const [waPhone, setWaPhone] = useState("908508884865");
  const wiggleTimer = useRef(null);

  // Fetch setting on mount
  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/settings`, { timeout: 4000 })
      .then((r) => {
        setShowWA(!!r.data?.show_whatsapp);
        if (r.data?.whatsapp_phone) setWaPhone(r.data.whatsapp_phone);
      })
      .catch(() => setShowWA(false));
  }, []);

  useEffect(() => {
    if (!showWA) return;
    const t1 = setTimeout(() => setVisible(true), 1400);
    return () => clearTimeout(t1);
  }, [showWA]);

  useEffect(() => {
    if (!visible) return;
    let scrolled = false;
    const onScroll = () => { scrolled = true; };
    window.addEventListener("scroll", onScroll, { passive: true });
    wiggleTimer.current = setTimeout(() => {
      if (!scrolled) {
        setWiggle(true);
        setTimeout(() => setWiggle(false), 1100);
      }
    }, 5200);
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (wiggleTimer.current) clearTimeout(wiggleTimer.current);
    };
  }, [visible]);

  useEffect(() => {
    const targets = Array.from(
      document.querySelectorAll(
        '[data-testid="lead-form"], [data-testid="cta-form"], #contact, #lead-form'
      )
    );
    if (targets.length === 0) {
      setHidden(false);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        const anyVisible = entries.some((e) => e.isIntersecting);
        setHidden(anyVisible);
      },
      { rootMargin: "0px 0px -10% 0px", threshold: 0.15 }
    );
    targets.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [pathname]);

  if (isAdmin || !showWA) return null;

  const buildHref = () => {
    const ctx = ` (Sayfa: ${pathname}, Dil: ${lang})`;
    return `https://wa.me/${waPhone}?text=${encodeURIComponent(t.msg + ctx)}`;
  };

  const handleClick = () => {
    try {
      axios.post(
        `${BACKEND_URL}/api/events/whatsapp_cta_click`,
        { lang, page: pathname, ts: new Date().toISOString() },
        { timeout: 3000 }
      ).catch(() => {});
    } catch (_) {}
  };

  return (
    <a
      href={buildHref()}
      target="_blank"
      rel="noopener noreferrer"
      onClick={handleClick}
      data-testid="whatsapp-floating-cta"
      aria-label={t.label}
      className={[
        "group fixed z-50 bottom-4 right-4 sm:bottom-6 sm:right-6",
        "transition-[opacity,transform] duration-500 ease-out",
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6 pointer-events-none",
        hidden ? "opacity-0 translate-y-2 pointer-events-none" : "",
        wiggle ? "animate-wiggle-once" : "",
      ].join(" ")}
      style={{ direction: "ltr" }}
    >
      <span aria-hidden="true" className="absolute inset-0 rounded-full bg-[#25D366] opacity-60 animate-ping-slow" />
      <span
        className="relative inline-flex items-center gap-2 sm:gap-2.5
                   bg-[#25D366] hover:bg-[#1FAB54] text-white
                   rounded-full
                   h-14 w-14 sm:w-auto sm:h-auto sm:px-5 sm:py-3.5
                   justify-center
                   shadow-[0_18px_40px_-10px_rgba(37,211,102,0.55)]
                   transition-[transform,background-color,box-shadow] duration-300 ease-out
                   group-hover:scale-105 group-active:scale-95"
      >
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" width="22" height="22" aria-hidden="true" className="flex-shrink-0">
          <path fill="currentColor" d="M16.003 3.2C9.07 3.2 3.46 8.81 3.46 15.74c0 2.4.65 4.7 1.88 6.74L3.2 28.8l6.5-2.1a12.5 12.5 0 0 0 6.3 1.7h.01c6.93 0 12.55-5.6 12.55-12.54.01-3.36-1.31-6.51-3.69-8.89A12.45 12.45 0 0 0 16.003 3.2Zm0 22.85h-.01a10.4 10.4 0 0 1-5.32-1.46l-.38-.23-3.86 1.25 1.27-3.78-.25-.4a10.42 10.42 0 0 1-1.6-5.55c0-5.78 4.7-10.48 10.5-10.48 2.8 0 5.43 1.09 7.41 3.07a10.4 10.4 0 0 1 3.07 7.42c0 5.78-4.71 10.46-10.5 10.46Zm5.74-7.84c-.31-.16-1.85-.91-2.13-1.02-.29-.1-.5-.16-.7.16-.21.31-.81 1.02-.99 1.23-.18.21-.36.24-.67.08-.31-.16-1.31-.48-2.49-1.54a9.36 9.36 0 0 1-1.73-2.15c-.18-.31-.02-.48.14-.63.14-.14.31-.36.47-.55.16-.18.21-.31.31-.52.11-.21.05-.39-.03-.55-.08-.16-.7-1.69-.96-2.31-.25-.6-.51-.52-.7-.53l-.6-.01c-.21 0-.55.08-.84.39-.29.31-1.1 1.08-1.1 2.62 0 1.55 1.13 3.05 1.29 3.26.16.21 2.22 3.39 5.39 4.76.75.32 1.34.52 1.79.66.75.24 1.43.21 1.97.13.6-.09 1.85-.76 2.11-1.49.26-.73.26-1.36.18-1.49-.08-.13-.29-.21-.6-.37Z" />
        </svg>
        <span className="hidden sm:inline text-sm font-bold whitespace-nowrap">
          {t.label}
        </span>
      </span>
    </a>
  );
}
